import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { getAutocompleteUsers, getFilterColumns, isLoadingUsers } from '../../core/store/selectors/filter-users.selector';
import { debounceTime } from 'rxjs/operators';
import { FilterUsers, GetFilterColumns, FilterUsersSuccess } from '../../core/store/actions/filter-user.actions';
import { DELAY_FILTER_USERS } from '../../../core/models/constants';
import { getSpecificUsersList } from '../store/selectors/current-user-groups.selector';
import { AddSpecificUser, DeleteSpecificUser, ModifySpecificUser } from '../store/actions/current-user-group.actions';
import { IAppState } from 'app/IAppState';

@Component({
    selector: 'user-groups-specific-user',
    templateUrl: 'user-groups-specific-user.component.html',
    styleUrls: ['user-groups-specific-user.component.scss']
})

export class UserGroupsSpecificUserComponent implements OnInit, OnDestroy {
    public valueToSearch: string;
    public selectedFilterColumn: string;
    public autocompleteUsers$: Observable<ISpecificUser[]>;
    public users$: Observable<Array<IUserGroupUser>>;
    public filterColumns$: Observable<string[]>;
    public isLoadingUsers$: Observable<boolean>;
    public selectedUser: ISpecificUser;
    private _searchDebounce = new Subject();
    private _searchDebounceSuscription: Subscription;
    private _filterColumnsSubscription: Subscription;

    public filterColumnsResources = {
        ContactEmail: 'USER_GROUPS.CONTACT_EMAIL',
        FullName: 'USER_GROUPS.FULLNAME',
        UserName: 'USER_GROUPS.USERNAME'
    };

    constructor(private _store: Store<IAppState>) {
        this.filterColumns$ = this._store.pipe(select(getFilterColumns));
        this.autocompleteUsers$ = this._store.pipe(select(getAutocompleteUsers));
        this.isLoadingUsers$ = this._store.pipe(select(isLoadingUsers));
        this.users$ = this._store.pipe(select(getSpecificUsersList));
    }

    ngOnInit() {
        this._initialize();
    }

    filter(valueToSearch: string) {
        this.valueToSearch = valueToSearch;
        if (this.valueToSearch) {
            this._searchDebounce.next();
        } else {
            this._store.dispatch(new FilterUsersSuccess([]));
        }
    }

    userSelected(event: any) {
        const user: IUserGroupUser = {
            id: event.value.idUser,
            include: false,
            userName: event.value.userName
        }
        this._store.dispatch(new AddSpecificUser(user));
    }

    displayFn(user?: ISpecificUser): string | undefined {
        return user ? user.fullName : undefined;
    }

    deleteUserFromTable(id: number) {
        this._store.dispatch(new DeleteSpecificUser(id));
    }

    modifyStatus(status: string, user: IUserGroupUser) {
        switch (status) {
            case 'include':
                if (!user.include) {
                    const modifyUser = { ...user, include: true };
                    this._store.dispatch(new ModifySpecificUser(modifyUser));
                }
                break;
            case 'exclude':
                if (user.include) {
                    const modifyUser = { ...user, include: false };
                    this._store.dispatch(new ModifySpecificUser(modifyUser));
                }
                break;
        }
    }

    ngOnDestroy() {
        this._store.dispatch(new FilterUsersSuccess([]));
        if (this._searchDebounceSuscription) {
            this._searchDebounceSuscription.unsubscribe();
        }
        if (this._filterColumnsSubscription) {
            this._filterColumnsSubscription.unsubscribe();
        }
    }

    private _initialize() {
        this._store.dispatch(new GetFilterColumns);
        this._searchDebounceSuscription = this._searchDebounce.pipe(debounceTime(DELAY_FILTER_USERS)).subscribe(() => {
            const valueToSearch = this.valueToSearch ? this.valueToSearch.trim() : this.valueToSearch;
            if (valueToSearch) {
                this._store.dispatch(new FilterUsers({ filterColumn: this.selectedFilterColumn, valueToSearch }));
            }
        });
        this._filterColumnsSubscription = this.filterColumns$.subscribe((columns: string[]) => {
            this.selectedFilterColumn = columns.length > 0
                ? columns.find((column: string) => {
                    return column === 'FullName'
                })
                : '';
        });
    }
}
